import apiClient from './api-client'

export interface Menu {
  id: number
  name: string
  display_name: string
  icon: string | null
  route_path: string
  module_name: string | null
  required_permission: string | null
  sort_order: number
  parent_id: number | null
  active: boolean
  visible: boolean
  metadata: Record<string, any> | null
  children?: Menu[]
}

export interface MenuFormData {
  name: string
  display_name: string
  icon?: string
  route_path: string
  module_name?: string
  required_permission?: string
  sort_order?: number
  parent_id?: number | null
  active?: boolean
  visible?: boolean
  metadata?: Record<string, any>
}

class MenuService {
  private cacheKey = 'user_menus'
  private cacheExpiry = 5 * 60 * 1000 // 5 minutes

  /**
   * Get user's accessible menus (from API)
   * @param forceRefresh - If true, bypass cache and fetch fresh data
   */
  async getUserMenus(forceRefresh: boolean = false): Promise<Menu[]> {
    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = this.getCachedMenus()
        if (cached) {
          return cached
        }
      }

      const response = await apiClient.get('/user/menus')
      const menus = response.data.data

      // Cache the menus with current OU
      this.cacheMenus(menus)

      return menus
    } catch (error) {
      console.error('Failed to fetch user menus:', error)
      throw error
    }
  }

  /**
   * Get all menus (admin only)
   */
  async getAllMenus(params?: { list?: boolean; per_page?: number }): Promise<any> {
    try {
      console.log('Fetching menus with params:', params)
      const response = await apiClient.get('/menus', { params })
      console.log('Menu API response:', response.data)

      // Handle different response structures
      if (response.data.status && response.data.data) {
        // If list=1, data is array directly
        // If paginated, data.data is the array
        const data = response.data.data
        const result = Array.isArray(data) ? data : data.data || []
        console.log('Processed menu data:', result)
        return result
      }

      console.warn('Unexpected API response structure:', response.data)
      return []
    } catch (error: any) {
      console.error('Failed to fetch all menus:', error)
      console.error('Error response:', error.response?.data)
      throw error
    }
  }

  /**
   * Get single menu
   */
  async getMenu(id: number): Promise<Menu> {
    try {
      const response = await apiClient.get(`/menus/${id}`)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch menu:', error)
      throw error
    }
  }

  /**
   * Create menu
   */
  async createMenu(data: MenuFormData): Promise<Menu> {
    try {
      const response = await apiClient.post('/menus', data)
      this.clearCache()
      return response.data.data
    } catch (error) {
      console.error('Failed to create menu:', error)
      throw error
    }
  }

  /**
   * Update menu
   */
  async updateMenu(id: number, data: Partial<MenuFormData>): Promise<Menu> {
    try {
      const response = await apiClient.put(`/menus/${id}`, data)
      this.clearCache()
      return response.data.data
    } catch (error) {
      console.error('Failed to update menu:', error)
      throw error
    }
  }

  /**
   * Delete menu
   */
  async deleteMenu(id: number): Promise<void> {
    try {
      await apiClient.delete(`/menus/${id}`)
      this.clearCache()
    } catch (error) {
      console.error('Failed to delete menu:', error)
      throw error
    }
  }

  /**
   * Reorder menus
   */
  async reorderMenus(menus: Array<{ id: number; sort_order: number }>): Promise<void> {
    try {
      await apiClient.post('/menus/reorder', { menus })
      this.clearCache()
    } catch (error) {
      console.error('Failed to reorder menus:', error)
      throw error
    }
  }

  /**
   * Cache menus in localStorage with OU context
   */
  private cacheMenus(menus: Menu[]) {
    const activeOU = localStorage.getItem('hris_active_ou') || ''
    const data = {
      menus,
      timestamp: Date.now(),
      ouCode: activeOU,
    }
    localStorage.setItem(this.cacheKey, JSON.stringify(data))
  }

  /**
   * Get cached menus if not expired and same OU
   */
  private getCachedMenus(): Menu[] | null {
    const cached = localStorage.getItem(this.cacheKey)
    if (!cached) return null

    try {
      const data = JSON.parse(cached)
      const age = Date.now() - data.timestamp
      const currentOU = localStorage.getItem('hris_active_ou') || ''

      // Check if cache is valid (not expired AND same OU)
      if (age < this.cacheExpiry && data.ouCode === currentOU) {
        return data.menus
      }
    } catch (error) {
      console.error('Failed to parse cached menus:', error)
    }

    return null
  }

  /**
   * Clear menu cache
   */
  clearCache() {
    localStorage.removeItem(this.cacheKey)
  }
}

export default new MenuService()
